<script lang="ts">
  /**
   * "Copy as [Markdown] [List]" row for a single task's ⋯ actions menu.
   *
   * Uses the user's saved copy settings (see TaskCopyFieldsPicker) and no `#`
   * heading, so the copy starts at the task's own `##` title. Font size follows
   * the host menu via `--copy-row-font-size`.
   */
  import type { Task } from '../lib/types';
  import { copyTasksWithToast, type TaskCopyFormat } from '../lib/taskClipboard';
  import Icon from './Icon.svelte';

  interface Props {
    task: Task;
    /** Called after copying, e.g. to close the host menu. */
    onDone?: () => void;
    /** Set when the host menu uses role="menu" so the buttons are menu items. */
    itemRole?: 'menuitem';
  }

  let { task, onDone, itemRole }: Props = $props();

  const OPTIONS: { format: TaskCopyFormat; label: string; ariaLabel: string }[] = [
    { format: 'markdown', label: 'Markdown', ariaLabel: 'Copy as Markdown' },
    { format: 'bulleted', label: 'List', ariaLabel: 'Copy as bulleted list' },
  ];

  function handleCopy(format: TaskCopyFormat) {
    onDone?.();
    void copyTasksWithToast([task], format);
  }
</script>

<div class="copy-row">
  <span class="copy-row-label"><Icon name="clipboard" />Copy as</span>
  {#each OPTIONS as option (option.format)}
    <button
      type="button"
      class="copy-row-btn"
      role={itemRole}
      aria-label={option.ariaLabel}
      title={option.ariaLabel}
      onclick={() => handleCopy(option.format)}
    >
      {option.label}
    </button>
  {/each}
</div>

<style>
  .copy-row {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    font-size: var(--copy-row-font-size, 0.6875rem);
    color: #374151;
    white-space: nowrap;
  }

  .copy-row-label {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-right: auto;
    padding-right: 0.25rem;
  }

  .copy-row-label :global(.icon) {
    color: #6b7280;
  }

  .copy-row-btn {
    padding: 0.125rem 0.375rem;
    font-family: inherit;
    font-size: 0.9em;
    font-weight: 500;
    color: #4b5563;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.1s;
  }

  .copy-row-btn:hover,
  .copy-row-btn:focus-visible {
    color: #1f2937;
    background: #f3f4f6;
    border-color: #d1d5db;
  }
</style>
